import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Segmented } from "@/components/ui/segmented";
import { ToastAction } from "@/components/ui/toast";
import { toast } from "@/components/ui/use-toast";
import { addClinicalTerm, getClinicalTerms, getOwnClinicalTerms, updateClinicalTerm } from "@/lib/api";
import { search, type ClinicalTerm, type TermKind } from "@/lib/clinicalTerms";
import { editDistance } from "@/lib/editDistance";
import { formatDate } from "@/lib/formatDate";
import { cn } from "@/lib/utils";
import { Search, Pencil, Trash2, Link2, X, Plus } from "lucide-react";

interface OwnTerm {
  id: number;
  name: string;
  active: boolean;
  /** The standard term this entry is marked the same as */
  mapsTo: { id: number; name: string; code: string | null } | null;
  uses: number;
  lastUsed: string | null;
}

type Changes = Parameters<typeof updateClinicalTerm>[1];

const WORDS: Record<TermKind, { one: string; many: string }> = {
  complaint: { one: "complaint", many: "complaints" },
  diagnosis: { one: "diagnosis", many: "diagnoses" },
  test:      { one: "test", many: "tests" },
};

// Your entry · Standard term · Used · Last used · actions
const GRID = "md:grid md:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)_56px_104px_72px] md:gap-x-3 md:items-center";

/** The standard term an entry most likely means: its name or a short form exactly, else a near spelling */
function likelyMatch(standard: ClinicalTerm[], name: string): ClinicalTerm | undefined {
  const n = name.trim().toLowerCase();
  const exact = standard.find(t => t.name.toLowerCase() === n || t.aliases.includes(n));
  if (exact || n.length < 5) return exact;
  // One slip in a short word, two in a longer one; short forms ("gad") are too short to judge
  let best: ClinicalTerm | undefined;
  let bestD = n.length >= 8 ? 3 : 2;
  for (const t of standard) {
    for (const s of [t.name.toLowerCase(), ...t.aliases]) {
      if (s.length < 5 || Math.abs(s.length - n.length) >= bestD) continue;
      const d = editDistance(n, s);
      if (d < bestD) { bestD = d; best = t; }
    }
  }
  return best;
}

/**
 * Presets → Complaints / Diagnoses / Tests: what the doctor wrote that isn't in the standard list. Each can
 * be marked the same as a standard term (suggested in its place, with its code), renamed, or removed.
 * Past prescriptions keep the text they printed whatever is done here.
 */
export default function OwnEntries({ kind, onChanged }: { kind: TermKind; onChanged?: () => void }) {
  const [entries, setEntries] = useState<OwnTerm[] | null>(null);
  const [standard, setStandard] = useState<ClinicalTerm[]>([]);
  const [view, setView] = useState<"active" | "removed">("active");
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [mapping, setMapping] = useState<{ id: number; query: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [adding, setAdding] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const w = WORDS[kind];

  const load = useCallback(async () => {
    try { setEntries((await getOwnClinicalTerms(kind)) ?? []); }
    catch { toast({ title: `Failed to load your ${w.many}`, variant: "destructive" }); setEntries([]); }
  }, [kind]);

  useEffect(() => {
    load();
    getClinicalTerms(kind).then(r => setStandard((r ?? []).filter((t: ClinicalTerm) => !t.own))).catch(() => {});
  }, [kind, load]);

  /** Applies a change; `undo` (when given) is offered in the toast */
  const change = async (e: OwnTerm, changes: Changes, title: string, undo?: Changes) => {
    setBusyId(e.id);
    try {
      await updateClinicalTerm(e.id, changes);
      setRenaming(null);
      setMapping(null);
      await load();
      onChanged?.();
      toast({
        title,
        // Long enough to reach Undo; plain confirmations close after the usual 2s
        duration: undo ? 6000 : undefined,
        action: undo && (
          <ToastAction altText="Undo" onClick={async () => { await updateClinicalTerm(e.id, undo); await load(); onChanged?.(); }}>Undo</ToastAction>
        ),
      });
    } catch (x: any) { toast({ title: x?.message || "Failed to save", variant: "destructive" }); }
    finally { setBusyId(null); }
  };

  const mapTo = (e: OwnTerm, t: { id: number; name: string }) =>
    change(e, { mapsTo: t.id }, `${e.name} is now the same as ${t.name}`, { mapsTo: e.mapsTo?.id ?? null });

  // What the add box would do with what's typed, checked as the doctor types
  const addCheck = useMemo(() => {
    const name = adding.trim();
    const n = name.toLowerCase();
    if (!name) return null;
    if (kind === "diagnosis" && name.includes(",")) return { block: true, hint: "A diagnosis name can't contain a comma. Add one at a time." };
    const standardTerm = standard.find(t => t.name.toLowerCase() === n || t.aliases.includes(n));
    if (standardTerm) return { block: true, hint: `Already suggested as ${standardTerm.name}${standardTerm.code ? ` (${standardTerm.code})` : ""}.` };
    const own = entries?.find(e => e.name.toLowerCase() === n);
    if (own?.active) return { block: true, hint: `You already have “${own.name}”.` };
    if (own) return { block: false, hint: `Press Enter to bring back your removed “${own.name}”.` };
    const near = likelyMatch(standard, name);
    if (near) return { block: false, hint: `Did you mean ${near.name}? It's already suggested. Press Enter to add “${name}” anyway.` };
    return { block: false, hint: `Press Enter to add “${name}” to your ${w.many}.` };
  }, [adding, standard, entries, kind]);

  const add = async () => {
    const name = adding.trim();
    if (!name || addCheck?.block || addBusy) return;
    setAddBusy(true);
    try {
      const r = await addClinicalTerm(kind, name);
      setAdding("");
      setView("active");
      await load();
      onChanged?.();
      toast({ title: r?.restored ? `${name} is back in your suggestions` : `${name} added to your ${w.many}` });
    } catch (x: any) { toast({ title: x?.message || "Failed to add", variant: "destructive" }); }
    finally { setAddBusy(false); }
  };

  const active = entries?.filter(e => e.active) ?? [];
  const removed = entries?.filter(e => !e.active) ?? [];
  // The box filters the list as the doctor types, and Enter adds what's typed
  const shown = (view === "active" ? active : removed)
    .filter(e => !adding.trim() || e.name.toLowerCase().includes(adding.trim().toLowerCase()));

  const likely = useMemo(
    () => new Map(active.filter(e => !e.mapsTo).map(e => [e.id, likelyMatch(standard, e.name)])),
    [entries, standard]
  );
  const matches = useMemo(
    () => (mapping ? search(standard, mapping.query).slice(0, 6) : []),
    [mapping, standard]
  );

  const standardCell = (e: OwnTerm) => {
    if (e.mapsTo) return (
      <span className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-primary/10 text-primary pl-2 text-sm">
        <span className="truncate">{e.mapsTo.name}</span>
        {e.mapsTo.code && <span className="font-mono text-2xs opacity-80">{e.mapsTo.code}</span>}
        {e.active && (
          <button
            type="button" disabled={busyId === e.id}
            onClick={() => change(e, { mapsTo: null }, `${e.name} is no longer the same as ${e.mapsTo!.name}`, { mapsTo: e.mapsTo!.id })}
            className="touch-target px-1.5 py-1 hover:text-foreground" title="Not the same" aria-label={`${e.name} is not the same as ${e.mapsTo.name}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
    if (!e.active) return <span className="hidden md:inline text-sm text-muted-foreground">—</span>;
    const guess = likely.get(e.id);
    return (
      <span className="flex items-center gap-2 min-w-0 text-sm">
        <button
          type="button" onClick={() => setMapping(mapping?.id === e.id ? null : { id: e.id, query: e.name })}
          className="touch-target inline-flex items-center gap-1 shrink-0 text-primary hover:underline underline-offset-2"
        >
          <Link2 className="w-3.5 h-3.5" />Same as…
        </button>
        {guess && (
          <button
            type="button" disabled={busyId === e.id} onClick={() => mapTo(e, guess)}
            className="touch-target min-w-0 truncate text-muted-foreground hover:text-foreground"
            title={`Mark ${e.name} the same as ${guess.name}`}
          >
            {guess.name}?
          </button>
        )}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Your own {w.many}, suggested as you type. Mark one the same as a standard {w.one} to get {kind === "diagnosis" ? "its ICD-10 code" : "the standard name"}.
      </p>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <form className="flex-1 min-w-[220px] flex items-center gap-2" onSubmit={ev => { ev.preventDefault(); add(); }}>
            <div className="flex-1 flex items-center h-8 touch:h-10 rounded-md border border-dashed border-field bg-card px-2.5 focus-within:border-ring focus-within:border-solid focus-within:ring-2 focus-within:ring-ring/20">
              <Plus className="w-3.5 h-3.5 mr-2 text-muted-foreground shrink-0" />
              <input
                value={adding} onChange={ev => setAdding(ev.target.value)}
                onKeyDown={ev => { if (ev.key === "Escape") setAdding(""); }}
                placeholder={`Search or add a ${w.one}…`} aria-label={`Search or add a ${w.one}`} maxLength={255}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
              />
              {adding && (
                <button type="button" onClick={() => setAdding("")} aria-label="Clear"
                  className="touch-target text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {adding.trim() && !addCheck?.block && (
              <Button type="submit" size="sm" className="h-8 touch:h-10" disabled={addBusy}>Add</Button>
            )}
          </form>
          <Segmented
            aria-label="Show"
            value={view}
            onChange={v => { setView(v as "active" | "removed"); setMapping(null); setRenaming(null); }}
            options={[
              { value: "active", label: <>In use <span className="tabular-nums text-muted-foreground">{active.length}</span></> },
              { value: "removed", label: <>Removed <span className="tabular-nums text-muted-foreground">{removed.length}</span></> },
            ]}
          />
        </div>
        {addCheck && <p className={cn("mt-1 px-1 text-xs", addCheck.block ? "text-foreground" : "text-muted-foreground")}>{addCheck.hint}</p>}
      </div>

      {entries === null ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {adding.trim() ? `None of your ${w.many} match “${adding.trim()}”.`
            : view === "removed" ? `Nothing removed. ${w.many[0].toUpperCase() + w.many.slice(1)} you take out of your suggestions show here.`
            : `Add ${w.many} you use that aren't in the standard list, or they'll show here when you write one on a prescription.`}
        </p>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className={cn("hidden h-8 px-3 bg-muted/40 border-b border-border text-xs font-medium text-label", GRID)}>
            <span>Your entry</span><span>Standard term</span><span className="text-right">Used</span><span>Last used</span><span />
          </div>
          <div className="divide-y divide-divider">
            {shown.map(e => {
              const busy = busyId === e.id;
              return (
                <div key={e.id} className="px-3 py-2 md:py-0">
                  <div className={cn("md:min-h-10", GRID)}>
                    <div className="min-w-0 flex items-center justify-between gap-2">
                      {renaming?.id === e.id ? (
                        <form
                          className="flex flex-1 items-center gap-1.5 py-1"
                          onSubmit={ev => { ev.preventDefault(); change(e, { name: renaming.name }, `Renamed to ${renaming.name.trim()}`, { name: e.name }); }}
                        >
                          <Input
                            autoFocus aria-label="New name" value={renaming.name}
                            onChange={ev => setRenaming({ id: e.id, name: ev.target.value })}
                            onKeyDown={ev => { if (ev.key === "Escape") setRenaming(null); }}
                            className="h-8 touch:h-10 text-sm"
                          />
                          <Button type="submit" size="sm" className="h-8 touch:h-10" disabled={busy || !renaming.name.trim() || renaming.name.trim() === e.name}>Save</Button>
                          <Button type="button" size="sm" variant="ghost" className="h-8 touch:h-10 w-8 p-0" onClick={() => setRenaming(null)} aria-label="Cancel rename">
                            <X className="w-4 h-4" />
                          </Button>
                        </form>
                      ) : (
                        <span className={cn("text-sm font-medium truncate", !e.active && "text-muted-foreground")}>{e.name}</span>
                      )}
                      <span className="md:hidden shrink-0">{actions(e, busy)}</span>
                    </div>
                    <div className="min-w-0 mt-1 md:mt-0">{standardCell(e)}</div>
                    <span className="hidden md:block text-right font-mono text-sm tabular-nums">{e.uses}</span>
                    <span className="hidden md:block text-sm text-muted-foreground tabular-nums whitespace-nowrap">{e.lastUsed ? formatDate(e.lastUsed) : "—"}</span>
                    <span className="hidden md:flex justify-end">{actions(e, busy)}</span>
                    <div className="md:hidden mt-0.5 text-xs text-muted-foreground">
                      {e.uses ? <>Used <span className="tabular-nums">{e.uses}</span>×{e.lastUsed && <> · last {formatDate(e.lastUsed)}</>}</> : "Not on a prescription yet"}
                    </div>
                  </div>

                  {mapping?.id === e.id && (
                    <div className="my-2 rounded-md border border-border bg-background p-2 space-y-1">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <Input
                          autoFocus aria-label={`Search the standard ${w.many}`}
                          placeholder={`Search the standard ${w.many}…`}
                          value={mapping.query}
                          onChange={ev => setMapping({ id: e.id, query: ev.target.value })}
                          onKeyDown={ev => { if (ev.key === "Escape") setMapping(null); }}
                          className="h-8 touch:h-10 pl-8 pr-9 text-sm"
                        />
                        <button type="button" onClick={() => setMapping(null)} aria-label="Close"
                          className="touch-target absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {matches.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-1 py-1.5">
                          {mapping.query.trim() ? "No standard term matches. Try another word, such as the English name." : `Type to search the standard ${w.many}.`}
                        </p>
                      ) : matches.map(({ term, alias }) => (
                        <button
                          key={term.id} type="button" disabled={busy} onClick={() => mapTo(e, term)}
                          className="w-full flex items-center gap-1.5 px-2 h-9 touch:h-10 text-left text-sm rounded hover:bg-accent"
                        >
                          <span className="truncate">{term.name}</span>
                          {alias && <span className="shrink-0 text-xs text-muted-foreground">· {alias}</span>}
                          {term.category && <span className="hidden sm:inline shrink-0 text-xs text-muted-foreground">· {term.category}</span>}
                          {term.code && <span className="shrink-0 ml-auto pl-2 font-mono text-2xs text-muted-foreground">{term.code}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  function actions(e: OwnTerm, busy: boolean) {
    if (!e.active) return (
      <Button size="sm" variant="outline" className="h-8 touch:h-10 text-xs" disabled={busy}
        onClick={() => change(e, { active: true }, `${e.name} is back in your suggestions`, { active: false })}>
        Restore
      </Button>
    );
    if (renaming?.id === e.id) return null;
    return (
      <span className="flex items-center gap-0.5">
        <Button
          variant="ghost" size="sm" className="h-8 w-8 touch:h-10 touch:w-10 p-0 text-muted-foreground hover:text-foreground"
          onClick={() => { setMapping(null); setRenaming({ id: e.id, name: e.name }); }}
          title="Rename" aria-label={`Rename ${e.name}`}
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost" size="sm" className="h-8 w-8 touch:h-10 touch:w-10 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
          disabled={busy}
          onClick={() => change(e, { active: false }, `${e.name} removed from your suggestions`, { active: true })}
          title="Remove from my suggestions" aria-label={`Remove ${e.name} from my suggestions`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </span>
    );
  }
}
